import type { PlaylistSortKey } from '@/lib/types/Playlist';

// --- as const arrays and derived types ---

export const LIST_TYPES = ['custom', 'tag'] as const;
export type ListType = typeof LIST_TYPES[number];

export const LIST_SORT_TYPES = ['published', 'title'] as const;
export type ListSortType = typeof LIST_SORT_TYPES[number];

export const TAG_LOGICAL_OPERATORS = ['and', 'or', 'not'] as const;
export type TagLogicalOperator = typeof TAG_LOGICAL_OPERATORS[number];

// --- ID mappings for embed URLs (content service routing) ---

/** Numeric IDs used in embed URL paths. Matches backend ListType.listTypeToId(). */
export const PLAYLIST_TYPE_IDS: Record<ListType, number> = {
  custom: 1,
  tag: 2,
} as const;

/** Numeric IDs for media types in embed URLs. Matches backend MediaTypeUtil.getId(). */
export const MEDIA_TYPE_IDS: Record<string, number> = {
  video: 1,
  audio: 2,
  image: 3,
  data: 4,
  video_stream: 5,
  audio_stream: 6,
} as const;

// --- Constraints ---

export const PLAYLIST_CONSTRAINTS = {
  TITLE_MIN: 1,
  TITLE_MAX: 150,
  DESC_SHORT_MAX: 500,
  REF_ID_MAX: 100,
  MAX_TAG_RULES: 3,
  MAX_LIMIT: 500,
} as const;

export const LIST_TYPE_LABELS = {
  custom: 'Manual',
  tag: 'Smart',
} as const;

export const TAG_LOP_LABELS = {
  and: 'has all',
  or: 'has any',
  not: 'does not have',
} as const;

export const LIST_SORT_UI_OPTIONS = [
  { value: 'published_desc', label: 'Published Date (Newest First)', sortType: 'published', sortDirection: 'desc' },
  { value: 'published_asc', label: 'Published Date (Oldest First)', sortType: 'published', sortDirection: 'asc' },
  { value: 'title_asc', label: 'Title (A-Z)', sortType: 'title', sortDirection: 'asc' },
] as const;

export const PLAYLIST_QUERY_KEYS = {
  all: (cId: number) => ['playlists', cId] as const,
  customDetail: (cId: number, listId: number) => ['playlists', cId, 'custom', listId] as const,
  tagDetail: (cId: number, listId: number) => ['playlists', cId, 'tag', listId] as const,
} as const;

export const PLAYLIST_GRID_COLUMNS = [
  { key: 'checkbox', label: '', sortable: false },
  { key: 'title', label: 'Title', sortable: true, sortKey: 'title' as PlaylistSortKey },
  { key: 'type', label: 'Type', sortable: true, sortKey: 'listType' as PlaylistSortKey },
  { key: 'status', label: 'Status', sortable: true, sortKey: 'state' as PlaylistSortKey },
  { key: 'created', label: 'Created', sortable: true, sortKey: 'created' as PlaylistSortKey },
  { key: 'updated', label: 'Updated', sortable: true, sortKey: 'updated' as PlaylistSortKey },
  { key: 'actions', label: '', sortable: false },
] as const;

export const DEFAULT_PLAYLIST_SORT_KEY: PlaylistSortKey = 'updated';
export const DEFAULT_PLAYLIST_SORT_DIRECTION = 'desc' as const;
export const PLAYLIST_SORT_KEYS = ['title', 'listType', 'state', 'created', 'updated'] as const;

export const PLAYLIST_ROW_HEIGHT = 56;
export const PLAYLIST_VIRTUALIZER_OVERSCAN = 10;
export const PLAYLIST_GRID_MAX_VIRTUAL_ROWS = 72;
export const PLAYLIST_GRID_MAX_VIEWPORT_HEIGHT_PX = 2200;

export const SEARCH_DEBOUNCE_MS = 200;
